import {
  AppAvatar,
  AppBox,
  AppButton,
  AppIconButton,
  AppLink,
  AppModal,
  AppPaper,
  AppText,
  AppTitle,
} from "../../../../commons/components";
import AppBorderBox from "../../../../commons/components/BorderBox";
import IconBin from "../../../../commons/components/icons/bin";
import IconEmpty from "../../../../commons/components/icons/empty";
import SkeletonGlobalCardList from "../../../../commons/components/skeleton/SkeletonGlobalCardList";
import { StatusEnum } from "../../../../commons/enums/status-enum";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import { useEffect, useState } from "react";
import OrganizerDelete from "./OrganizerDelete";
import OrganizerDeleteInprogress from "./OrganizerDeleteInprogress";
import { useSnackbar } from "../../../../redux/snackbarProvider";
import setDocumentTitle from "../../../../commons/utilities/document-title";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../commons/components/interface";
import FootballOrganizerAddEdit from "./organizerAddEdit/OrganizerAddEdit";
import IconEdit from "../../../../commons/components/icons/edit";
import { toTitleCase } from "../../../../commons/utilities/string-utils";
import { cleanUpStepperFormData } from "../../../../redux/slices/stepper-form-slice";
import { Can } from "../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../commons/enums/role-policy-action.enum";
import useFilter from "../../../../hooks/useFilter";
import FilterToolbar from "../../../../commons/components/ui-components/FilterToolbar";
import { FootballOrganizer } from "../../../../commons/models/football/football-organizer";
import { fetchManageFootballOrganizerList } from "../../../../redux/slices/football/admin/organizer/manage-football-organizer-list-slice";
import { footballOrganizerDelete } from "../../../../redux/slices/football/admin/organizer/manage-football-organizer-delete-slice";

export default function FootballOrganizerList() {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { footballOrganizerList, footballOrganizerListStatus } = useAppSelector(
    (state) => state.manageFootballOrganizerList
  );
  const { footballOrganizerDeleteStatus } = useAppSelector((state) => state.manageFootballOrganizerDelete);
  const [isDeleteFootballOrganizer, setDeleteFootballOrganizer] = useState(false);
  const [isAddEditFootballOrganizer, setAddEditFootballOrganizer] = useState(false);
  const [selectedFootballOrganizer, setSelectedFootballOrganizer] = useState<FootballOrganizer | null>(null);
  const { sortBy, sortOrder, searchText } = useFilter();
  const { footballOrganizerAddStatus } = useAppSelector((state) => state.manageFootballOrganizerAdd);
  const { footballOrganizerEditStatus } = useAppSelector((state) => state.manageFootballOrganizerEdit);

  const fetchFootballOrganizerList = () => {
    dispatch(fetchManageFootballOrganizerList({ sort: sortBy, sortOrder, name: searchText }));
  };
  const handleDeleteClick = (footballOrganizer: FootballOrganizer) => (event: any) => {
    setSelectedFootballOrganizer(footballOrganizer);
    setDeleteFootballOrganizer(true);
  };

  const handleEditClick = (footballOrganizer: FootballOrganizer) => (event: any) => {
    setSelectedFootballOrganizer(footballOrganizer);
    setAddEditFootballOrganizer(true);
  };

  const handleAfterAddEdit = () => {
    setSelectedFootballOrganizer(null);
    setAddEditFootballOrganizer(false);
    fetchFootballOrganizerList();
  };

  const handleAfterAddEditClose = () => {
    setSelectedFootballOrganizer(null);
    setAddEditFootballOrganizer(false);
    dispatch(cleanUpStepperFormData());
  };

  const handleDeleteConfirm = (e: Event) => {
    e.preventDefault();
    if (selectedFootballOrganizer) {
      setDeleteFootballOrganizer(false);
      dispatch(footballOrganizerDelete(selectedFootballOrganizer.id))
        .unwrap()
        .then(() => {
          addSnackbar({
            key: "footballOrganizer-delete-success",
            text: "Organizer Deleted Successfully",
            variant: "success",
          });
          setSelectedFootballOrganizer(null);
          fetchFootballOrganizerList();
        })
        .catch((error: AxiosError<IServerErrorResponse>) => {
          addSnackbar({
            key: "error",
            text: error.response?.data.message,
            variant: "danger",
          });
        });
    }
  };

  const handleClick = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    setDocumentTitle(["Organizers", "Manage"]);
  }, [footballOrganizerList]);

  return (
    <>
      <AppBox flexDirection="column" gap="xs">
        <AppBox alignItems="center" justifyContent="space-between">
          <AppTitle as="h4">All Organizers</AppTitle>
          <Can I={RolePolicyActionEnum.CREATE} a={FootballOrganizer}>
            {footballOrganizerList.length > 0 && (
              <AppButton label="Add Organizer" onClick={() => setAddEditFootballOrganizer(true)} />
            )}
          </Can>
        </AppBox>
        <FilterToolbar enableStatus={false} />
        {footballOrganizerListStatus === StatusEnum.Loading ? (
          <SkeletonGlobalCardList noTitle numberOfItems={10} withAvatar withBadge />
        ) : (
          <>
            {footballOrganizerList.length === 0 ? (
              <AppPaper style={{ height: "100%" }}>
                <AppBorderBox borderVariant="dashed">
                  <AppBox
                    flexDirection="column"
                    alignItems="center"
                    gap="sm"
                    justifyContent="center"
                    style={{ height: "calc(90vh - 16rem)" }}
                  >
                    <AppBox alignItems="center" flexDirection="column" justifyContent="center" gap="2xs">
                      <IconEmpty width={56} height={56} color="rgba(var(--border-200))" />
                      <AppTitle as="h6">No Organizers Found</AppTitle>
                      <Can I={RolePolicyActionEnum.CREATE} a={FootballOrganizer}>
                        <AppText>Ready to kick things off? Let's get started by adding organizers.</AppText>
                      </Can>
                    </AppBox>
                    <Can I={RolePolicyActionEnum.CREATE} a={FootballOrganizer}>
                      <AppButton label="Add New Organizer" onClick={() => setAddEditFootballOrganizer(true)} />
                    </Can>
                  </AppBox>
                </AppBorderBox>
              </AppPaper>
            ) : (
              <>
                {footballOrganizerList.mapArray((footballOrganizer, index) => (
                  <AppPaper radius="sm" padding="none" shadow="xs" key={index}>
                    <AppBox gap="xs" my="2xs" mx="xs" justifyContent="space-between">
                      <AppLink to={`/manage/organizers/${footballOrganizer.id}`} onClick={handleClick}>
                        <AppBox gap="xs" alignItems="center">
                          <AppAvatar username={footballOrganizer.name} src={footballOrganizer.logo} size="lg" />
                          <AppTitle as={"h5"}>{toTitleCase(footballOrganizer.name)}</AppTitle>
                        </AppBox>
                      </AppLink>
                      <AppBox gap="xs" justifyContent="end" alignItems="center">
                        <Can I={RolePolicyActionEnum.UPDATE} a={FootballOrganizer}>
                          <AppIconButton
                            icon={<IconEdit />}
                            variant="light"
                            onClick={handleEditClick(footballOrganizer)}
                          />
                        </Can>
                        <Can I={RolePolicyActionEnum.DELETE} a={FootballOrganizer}>
                          <AppIconButton
                            icon={<IconBin />}
                            variant="light"
                            color="danger"
                            onClick={handleDeleteClick(footballOrganizer)}
                          />
                        </Can>
                      </AppBox>
                    </AppBox>
                  </AppPaper>
                ))}
              </>
            )}
          </>
        )}
      </AppBox>
      <AppModal
        opened={isAddEditFootballOrganizer}
        modalSize="lg"
        onClose={handleAfterAddEditClose}
        title={selectedFootballOrganizer ? "Edit Organizer" : "Add New Organizer"}
        titleIcon={selectedFootballOrganizer ? <IconEdit /> : ""}
        withoutButtons
        withLoadingButton={
          footballOrganizerAddStatus === StatusEnum.Loading || footballOrganizerEditStatus === StatusEnum.Loading
        }
      >
        <FootballOrganizerAddEdit
          footballOrganizerData={selectedFootballOrganizer}
          onSave={handleAfterAddEdit}
          onCancel={handleAfterAddEditClose}
        />
      </AppModal>
      {selectedFootballOrganizer && (
        <>
          <AppModal
            opened={isDeleteFootballOrganizer}
            onClose={(e) => {
              setSelectedFootballOrganizer(null);
              setDeleteFootballOrganizer(false);
            }}
            title="Want To Delete This Organizer?"
            titleIcon={<IconBin />}
            iconColor="danger"
            confirmButtonLabel="Delete"
            onSave={(e) => {
              handleDeleteConfirm(e as Event);
            }}
          >
            <OrganizerDelete organizer={selectedFootballOrganizer} />
          </AppModal>
          <AppModal
            opened={footballOrganizerDeleteStatus === StatusEnum.Loading}
            title="Deleting Organizer"
            titleIcon={<IconBin />}
            iconColor="danger"
            withoutCancelButton
            withoutButtons
            withoutCloseButton
          >
            <OrganizerDeleteInprogress organizer={selectedFootballOrganizer} />
          </AppModal>
        </>
      )}
    </>
  );
}
